(() => {
  'use strict';

  const root = document.querySelector('[data-vel-reservation]');
  if (!root) return;

  const endpoint = root.dataset.endpoint || '';
  const token = root.dataset.endpointToken || '';
  const confirmationUrl = root.dataset.confirmationUrl || '/pages/reservation-confirmee';
  const offerUrl = root.dataset.offerUrl || '/';
  const currency = root.dataset.currency || 'EUR';
  const form = root.querySelector('[data-reservation-form]');
  const emptyState = root.querySelector('[data-empty-cart]');
  const cartItemsNode = root.querySelector('[data-cart-items]');
  const subtotalNode = root.querySelector('[data-cart-subtotal]');
  const totalNode = root.querySelector('[data-cart-total]');
  const reviewNode = root.querySelector('[data-reservation-review]');
  const submitButton = root.querySelector('[data-submit-reservation]');
  const submitError = root.querySelector('[data-submit-error]');

  const STORAGE_KEY = 'veloure_reservation_draft_v1';
  const ATTRIBUTION_KEY = 'veloure_attribution_v1';
  const SESSION_KEY = 'veloure_reservation_session_v1';
  const LAST_RESERVATION_KEY = 'veloure_last_reservation_v1';
  const ENTRY_KEY = 'veloure_reservation_entry_v1';
  const startedAt = Date.now();

  const state = {
    step: 1,
    cart: null,
    submitted: false,
    sessionId: sessionStorage.getItem(SESSION_KEY) || createId('VRS'),
    attribution: captureAttribution(),
    entry: safeParse(localStorage.getItem(ENTRY_KEY)) || {}
  };
  sessionStorage.setItem(SESSION_KEY, state.sessionId);

  function createId(prefix) {
    const random = Math.random().toString(36).slice(2, 8).toUpperCase();
    return `${prefix}-${Date.now().toString(36).toUpperCase()}-${random}`;
  }

  function captureAttribution() {
    const params = new URLSearchParams(window.location.search);
    const previous = safeParse(localStorage.getItem(ATTRIBUTION_KEY)) || {};
    const keys = ['utm_source','utm_medium','utm_campaign','utm_content','utm_term','fbclid','gclid','ttclid'];
    const next = { ...previous };
    keys.forEach(key => {
      const value = params.get(key);
      if (value) next[key] = value;
    });
    if (!next.first_landing_page) next.first_landing_page = previous.first_landing_page || document.referrer || window.location.href;
    next.latest_landing_page = window.location.href;
    next.referrer = document.referrer || previous.referrer || '';
    localStorage.setItem(ATTRIBUTION_KEY, JSON.stringify(next));
    return next;
  }

  function safeParse(value) {
    try { return JSON.parse(value); } catch (_) { return null; }
  }

  function formatMoney(cents) {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency }).format((Number(cents) || 0) / 100);
  }

  function escapeHtml(value) {
    return String(value ?? '')
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');
  }

  async function loadCart() {
    try {
      const response = await fetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('Cart request failed');
      state.cart = await response.json();
      if (!state.cart.items?.length) {
        form.hidden = true;
        emptyState.hidden = false;
        root.querySelector('.vel-reservation__summary').hidden = true;
        track('reservation_cart_empty');
        return;
      }
      renderCart();
      restoreDraft();
      renderReview();
      track('reservation_page_viewed', { cart_value: state.cart.total_price / 100, item_count: state.cart.item_count, entry_source: state.entry.source || 'direct', entry_id: state.entry.entry_id || '' });
    } catch (error) {
      showSubmitError('Impossible de charger votre sélection. Rechargez la page ou revenez à l’offre.');
      track('reservation_cart_load_error', { message: error.message });
    }
  }

  function renderCart() {
    cartItemsNode.innerHTML = state.cart.items.map(item => {
      const properties = Object.entries(item.properties || {})
        .filter(([key, value]) => value && !key.startsWith('_'))
        .map(([key, value]) => `${escapeHtml(key)} : ${escapeHtml(value)}`)
        .join('<br>');
      const variant = item.variant_title && item.variant_title !== 'Default Title' ? escapeHtml(item.variant_title) : '';
      return `
        <article class="vel-reservation__item">
          ${item.image ? `<img class="vel-reservation__item-image" src="${escapeHtml(item.image)}" alt="${escapeHtml(item.product_title)}" loading="eager">` : '<div class="vel-reservation__item-image"></div>'}
          <div class="vel-reservation__item-copy">
            <h3>${escapeHtml(item.product_title)}</h3>
            <p>${variant}${variant && properties ? '<br>' : ''}${properties || ''}<br>Quantité : ${item.quantity}</p>
          </div>
          <div class="vel-reservation__item-price">${formatMoney(item.final_line_price)}</div>
        </article>`;
    }).join('');
    subtotalNode.textContent = formatMoney(state.cart.items_subtotal_price);
    totalNode.textContent = formatMoney(state.cart.total_price);
  }

  function renderReview() {
    if (!reviewNode || !state.cart) return;
    const data = getFormData();
    reviewNode.innerHTML = `
      <div class="vel-reservation__review-row"><span>Client</span><strong>${escapeHtml(`${data.first_name || ''} ${data.last_name || ''}`.trim() || '—')}</strong></div>
      <div class="vel-reservation__review-row"><span>E-mail</span><strong>${escapeHtml(data.email || '—')}</strong></div>
      <div class="vel-reservation__review-row"><span>Livraison</span><strong>${escapeHtml([data.address1, data.address2, data.postal_code, data.city, data.country].filter(Boolean).join(', ') || '—')}</strong></div>
      <div class="vel-reservation__review-row"><span>Total prévu</span><strong>${formatMoney(state.cart.total_price)}</strong></div>
      <div class="vel-reservation__review-row"><span>Paiement aujourd’hui</span><strong>0,00 €</strong></div>`;
  }

  function getFormData() {
    const data = Object.fromEntries(new FormData(form).entries());
    return {
      first_name: String(data.first_name || '').trim(),
      last_name: String(data.last_name || '').trim(),
      email: String(data.email || '').trim().toLowerCase(),
      phone: String(data.phone || '').trim(),
      country: String(data.country || 'France').trim(),
      address1: String(data.address1 || '').trim(),
      address2: String(data.address2 || '').trim(),
      postal_code: String(data.postal_code || '').trim(),
      city: String(data.city || '').trim(),
      reservation_consent: data.reservation_consent === 'on',
      marketing_consent: data.marketing_consent === 'on',
      website: String(data.website || '').trim()
    };
  }

  function validateStep(step) {
    clearErrors();
    const data = getFormData();
    const errors = {};
    if (step === 1) {
      if (data.first_name.length < 2) errors.first_name = 'Indiquez votre prénom.';
      if (data.last_name.length < 2) errors.last_name = 'Indiquez votre nom.';
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) errors.email = 'Indiquez une adresse e-mail valide.';
      if (data.phone && data.phone.replace(/\D/g, '').length < 8) errors.phone = 'Vérifiez le numéro de téléphone.';
    }
    if (step === 2) {
      if (data.address1.length < 5) errors.address1 = 'Indiquez une adresse complète.';
      if (!/^\d{5}$/.test(data.postal_code)) errors.postal_code = 'Le code postal français doit contenir 5 chiffres.';
      if (data.city.length < 2) errors.city = 'Indiquez votre ville.';
    }
    if (step === 3 && !data.reservation_consent) errors.reservation_consent = 'Cette confirmation est obligatoire.';
    Object.entries(errors).forEach(([name, message]) => showFieldError(name, message));
    if (Object.keys(errors).length) {
      track('reservation_validation_error', { step, fields: Object.keys(errors) });
      const first = form.querySelector(`[name="${Object.keys(errors)[0]}"]`);
      first?.focus();
      return false;
    }
    return true;
  }

  function showFieldError(name, message) {
    const field = form.querySelector(`[name="${name}"]`);
    field?.classList.add('is-invalid');
    field?.setAttribute('aria-invalid', 'true');
    const error = root.querySelector(`[data-error-for="${name}"]`);
    if (error) error.textContent = message;
  }

  function clearErrors() {
    form.querySelectorAll('.is-invalid').forEach(node => {
      node.classList.remove('is-invalid');
      node.removeAttribute('aria-invalid');
    });
    root.querySelectorAll('[data-error-for]').forEach(node => { node.textContent = ''; });
    hideSubmitError();
  }

  function goToStep(nextStep) {
    const previousStep = state.step;
    state.step = nextStep;
    root.querySelectorAll('[data-step-panel]').forEach(panel => {
      const active = Number(panel.dataset.stepPanel) === nextStep;
      panel.hidden = !active;
      panel.classList.toggle('is-active', active);
    });
    const progressSteps = [...root.querySelectorAll('[data-progress-step]')];
    const bars = [...root.querySelectorAll('.vel-reservation__progress > i')];
    progressSteps.forEach(button => {
      const number = Number(button.dataset.progressStep);
      button.classList.toggle('is-active', number === nextStep);
      button.classList.toggle('is-complete', number < nextStep);
      button.disabled = number > nextStep;
      if (number === nextStep) button.setAttribute('aria-current', 'step'); else button.removeAttribute('aria-current');
    });
    bars.forEach((bar, index) => bar.classList.toggle('is-complete', index + 1 < nextStep));
    saveDraft();
    if (nextStep === 3) renderReview();
    track('reservation_step_viewed', { step: nextStep, previous_step: previousStep });
    window.scrollTo({ top: Math.max(0, root.querySelector('.vel-reservation__grid').getBoundingClientRect().top + window.scrollY - 20), behavior: 'smooth' });
  }

  function saveDraft() {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ form: getFormData(), step: state.step, saved_at: new Date().toISOString() }));
  }

  function restoreDraft() {
    const draft = safeParse(localStorage.getItem(STORAGE_KEY));
    if (!draft?.form) return;
    Object.entries(draft.form).forEach(([name, value]) => {
      const field = form.elements.namedItem(name);
      if (!field) return;
      if (field.type === 'checkbox') field.checked = Boolean(value); else if (name !== 'website') field.value = value || '';
    });
  }

  function clientContext() {
    return {
      page_url: window.location.href,
      page_path: window.location.pathname,
      referrer: document.referrer || '',
      language: navigator.language || '',
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || '',
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      screen: `${window.screen?.width || 0}x${window.screen?.height || 0}`,
      user_agent: navigator.userAgent,
      elapsed_ms: Date.now() - startedAt
    };
  }

  function cartPayload() {
    return {
      token: state.cart.token,
      item_count: state.cart.item_count,
      total_price: state.cart.total_price,
      items_subtotal_price: state.cart.items_subtotal_price,
      currency,
      items: state.cart.items.map(item => ({
        product_id: item.product_id,
        variant_id: item.variant_id,
        sku: item.sku || '',
        product_title: item.product_title,
        variant_title: item.variant_title || '',
        quantity: item.quantity,
        unit_price: item.final_price,
        line_price: item.final_line_price,
        properties: item.properties || {},
        image: item.image || ''
      }))
    };
  }

  async function postPayload(payload) {
    if (!endpoint) throw new Error('L’URL du Web App n’est pas configurée.');
    await fetch(endpoint, {
      method: 'POST',
      mode: 'no-cors',
      keepalive: true,
      headers: { 'Content-Type': 'text/plain;charset=utf-8' },
      body: JSON.stringify({ ...payload, token })
    });
  }


  function jsonpStatus(reservationId, attempt = 0) {
    return new Promise((resolve, reject) => {
      if (!endpoint) return reject(new Error('Endpoint manquant'));
      const callbackName = `veloureReservationStatus_${Date.now()}_${attempt}`;
      const script = document.createElement('script');
      const timeout = window.setTimeout(() => cleanup(new Error('Délai de confirmation dépassé')), 5000);
      function cleanup(error, result) {
        window.clearTimeout(timeout);
        delete window[callbackName];
        script.remove();
        if (error) reject(error); else resolve(result);
      }
      window[callbackName] = result => cleanup(null, result);
      const url = new URL(endpoint);
      url.searchParams.set('mode', 'status');
      url.searchParams.set('rid', reservationId);
      url.searchParams.set('token', token);
      url.searchParams.set('callback', callbackName);
      url.searchParams.set('_', String(Date.now()));
      script.onerror = () => cleanup(new Error('La vérification de la réservation a échoué'));
      script.src = url.toString();
      document.head.appendChild(script);
    });
  }

  async function waitForReservation(reservationId) {
    for (let attempt = 0; attempt < 7; attempt += 1) {
      try {
        const result = await jsonpStatus(reservationId, attempt);
        if (result && result.ok && result.found) return true;
      } catch (_) {}
      await new Promise(resolve => window.setTimeout(resolve, 700 + attempt * 250));
    }
    return false;
  }

  function track(eventName, data = {}) {
    const payload = {
      type: 'event',
      event_name: eventName,
      event_id: createId('EVT'),
      session_id: state.sessionId,
      step: state.step,
      occurred_at: new Date().toISOString(),
      attribution: state.attribution,
      entry: state.entry,
      client: clientContext(),
      data
    };
    if (endpoint) postPayload(payload).catch(() => {});
    try { window.Shopify?.analytics?.publish?.(eventName, { session_id: state.sessionId, step: state.step, ...data }); } catch (_) {}
  }

  function showSubmitError(message) {
    submitError.textContent = message;
    submitError.hidden = false;
  }

  function hideSubmitError() {
    submitError.textContent = '';
    submitError.hidden = true;
  }

  function setSubmitting(active) {
    submitButton.disabled = active;
    submitButton.querySelector('.vel-reservation__button-text').textContent = active ? 'Enregistrement…' : 'Confirmer ma réservation';
    submitButton.querySelector('.vel-reservation__spinner').hidden = !active;
  }

  root.querySelectorAll('[data-next-step]').forEach(button => {
    button.addEventListener('click', () => {
      const next = Number(button.dataset.nextStep);
      if (!validateStep(state.step)) return;
      track('reservation_step_completed', { step: state.step });
      goToStep(next);
    });
  });

  root.querySelectorAll('[data-prev-step]').forEach(button => {
    button.addEventListener('click', () => goToStep(Number(button.dataset.prevStep)));
  });

  root.querySelectorAll('[data-progress-step]').forEach(button => {
    button.addEventListener('click', () => {
      const target = Number(button.dataset.progressStep);
      if (target < state.step) goToStep(target);
    });
  });

  form.addEventListener('input', saveDraft);
  form.addEventListener('change', saveDraft);

  form.addEventListener('submit', async event => {
    event.preventDefault();
    if (!validateStep(3) || !state.cart?.items?.length) return;
    const data = getFormData();
    if (data.website) return;
    if (Date.now() - startedAt < 2500) {
      showSubmitError('Veuillez vérifier vos informations avant de confirmer.');
      return;
    }

    setSubmitting(true);
    hideSubmitError();
    const reservationId = createId('VEL-R');
    const payload = {
      type: 'reservation',
      reservation_id: reservationId,
      session_id: state.sessionId,
      created_at: new Date().toISOString(),
      customer: {
        first_name: data.first_name,
        last_name: data.last_name,
        email: data.email,
        phone: data.phone,
        address1: data.address1,
        address2: data.address2,
        postal_code: data.postal_code,
        city: data.city,
        country: data.country
      },
      consent: {
        reservation: data.reservation_consent,
        marketing: data.marketing_consent
      },
      cart: cartPayload(),
      attribution: state.attribution,
      entry: state.entry,
      client: clientContext()
    };

    track('reservation_submit_started', { reservation_id: reservationId, value: state.cart.total_price / 100, currency });

    try {
      await postPayload(payload);
      const confirmedByBackend = await waitForReservation(reservationId);
      if (!confirmedByBackend) throw new Error('Nous n’avons pas pu confirmer l’enregistrement. Vos informations sont conservées sur cet appareil : réessayez sans fermer la page.');
      state.submitted = true;
      const stored = { ...payload, customer: { ...payload.customer, address1: data.address1, address2: data.address2 } };
      localStorage.setItem(LAST_RESERVATION_KEY, JSON.stringify(stored));
      localStorage.removeItem(STORAGE_KEY);
      track('reservation_completed', {
        reservation_id: reservationId,
        value: state.cart.total_price / 100,
        currency,
        item_count: state.cart.item_count
      });
      try { await fetch('/cart/clear.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' }); } catch (_) {}
      const url = new URL(confirmationUrl, window.location.origin);
      url.searchParams.set('rid', reservationId);
      window.location.assign(url.toString());
    } catch (error) {
      showSubmitError(error.message || 'La réservation n’a pas pu être enregistrée. Réessayez dans quelques instants.');
      track('reservation_submit_error', { message: error.message || 'unknown' });
      setSubmitting(false);
    }
  });

  root.querySelector('[data-edit-offer]')?.addEventListener('click', () => track('reservation_edit_offer_clicked'));

  window.addEventListener('pagehide', () => {
    if (state.submitted || !endpoint) return;
    const payload = {
      type: 'event', event_name: 'reservation_abandoned', event_id: createId('EVT'), session_id: state.sessionId,
      step: state.step, occurred_at: new Date().toISOString(), attribution: state.attribution, client: clientContext(),
      data: { cart_value: state.cart ? state.cart.total_price / 100 : 0 }
    };
    try {
      navigator.sendBeacon(endpoint, new Blob([JSON.stringify({ ...payload, token })], { type: 'text/plain;charset=utf-8' }));
    } catch (_) {}
  });

  loadCart();
})();
